import React, { useState, useMemo, useRef, useEffect } from 'react';
import { isActualDisplayableResultItem } from '../utils/helpers';
import { Database, HardDrive, ChevronDown } from 'lucide-react';

const CustomDbDropdown = ({ groupName, groupState, onSourceToggle, onCustomFolderToggle, subfolders = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownRef]);

  const isEnabled = groupState?.enabled;
  const foldersState = groupState?.folders || {};

  return (
    <li ref={dropdownRef} className="mb-0.5">
      <button
        className={`w-full flex items-center gap-2.5 px-3 py-2 mx-2 rounded-lg text-[13px] cursor-pointer transition-all duration-150 relative ${isEnabled ? 'bg-indigo-500/10' : ''}`}
        style={{ color: isEnabled ? 'var(--text-primary)' : 'var(--text-muted)' }}
        onClick={() => setIsOpen(prev => !prev)}
      >
        {isEnabled && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 bg-indigo-500 rounded-r-full" />}
        <HardDrive size={14} className={isEnabled ? "text-indigo-400" : ""} style={!isEnabled ? { color: 'var(--text-faint)' } : {}} />
        <span className="flex-1 text-left truncate">{groupName}</span>
        <ChevronDown size={14} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="flex flex-col gap-0.5 mt-1 mx-2 pl-6">
          <label className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer rounded-md transition-colors" style={{ color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={!!isEnabled} onChange={() => onSourceToggle(groupName)} className="accent-indigo-500 rounded" />
            Enable All Custom Databases
          </label>
          <div className="h-px my-1 mx-2" style={{ backgroundColor: 'var(--border-primary)' }} />
          {subfolders.map(folderName => (
            <label key={folderName} className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer rounded-md transition-colors" style={{ color: 'var(--text-muted)' }}>
              <input
                type="checkbox"
                checked={!!foldersState[folderName]}
                onChange={() => onCustomFolderToggle(folderName)}
                disabled={!isEnabled}
                className="accent-indigo-500 rounded"
              />
              {folderName}
            </label>
          ))}
        </div>
      )}
    </li>
  );
};

const StatusPanel = ({
  allSitePackages, allSourcesConfig, enabledSources, onSourceToggle,
  onSelectAll, onDeselectAll, sourcesStatus, onCustomFolderToggle
}) => {
  const orderedSources = useMemo(() => {
    if (!allSourcesConfig.length) return [];
    
    const grouped = allSourcesConfig.reduce((acc, source) => {
      const group = source.display_group;
      if (!acc[group]) acc[group] = { sources: [], sourceNames: [], subfolders: source.subfolders };
      acc[group].sources.push(source);
      acc[group].sourceNames.push(source.source_name);
      return acc;
    }, {});

    const entries = Object.entries(grouped);
    const customDbIndex = entries.findIndex(([groupName]) => groupName === 'Custom Databases');
    
    let customDbEntry = null;
    if (customDbIndex > -1) {
      customDbEntry = entries.splice(customDbIndex, 1)[0];
    }

    entries.sort((a, b) => a[0].localeCompare(b[0]));

    if (customDbEntry) {
      entries.push(customDbEntry);
    }

    return entries;
  }, [allSourcesConfig]);

  // Check if a group has a local database file loaded
  // sourcesStatus is keyed by source_name (e.g. "Austin"), not display_group ("Austin Pathology")
  // So we need to check all source_names in the group
  const hasLocalData = (groupInfo) => {
    return groupInfo.sourceNames.some(sourceName => {
      const status = sourcesStatus[sourceName];
      return status && status.file_count > 0;
    });
  };

  const getLiveSearchStatus = (groupName) => {
    const packages = allSitePackages.filter(p => p.display_group === groupName);
    if (packages.length === 0) return 'pending';
    if (packages.some(p => p.status === 'success' && p.results.some(isActualDisplayableResultItem))) return 'success';
    if (packages.some(p => p.status === 'error')) return 'error';
    return 'no_match';
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 flex gap-1.5" style={{ borderBottom: '1px solid var(--border-primary)' }}>
        <button className="text-xs py-1.5 px-3 rounded-lg transition-all duration-150" style={{ border: '1px solid var(--border-secondary)', color: 'var(--text-muted)' }} onClick={onSelectAll}>Select All</button>
        <button className="text-xs py-1.5 px-3 rounded-lg transition-all duration-150" style={{ border: '1px solid var(--border-secondary)', color: 'var(--text-muted)' }} onClick={onDeselectAll}>Deselect All</button>
      </div>
      
      <div className="py-2 overflow-y-auto flex-1 custom-scrollbar">
        <div className="px-4 pb-1.5 text-[10px] uppercase tracking-widest font-medium mt-2" style={{ color: 'var(--text-faint)' }}>Databases</div>
        <ul className="flex flex-col pr-2">
          {orderedSources.map(([groupName, groupInfo]) => {
            if (groupName === 'Custom Databases') {
              return (
                <CustomDbDropdown
                  key={groupName}
                  groupName={groupName}
                  groupState={enabledSources[groupName]}
                  onSourceToggle={onSourceToggle}
                  onCustomFolderToggle={onCustomFolderToggle}
                  subfolders={groupInfo.subfolders || []}
                />
              );
            }

            const isEnabled = !!enabledSources[groupName];
            const liveStatus = getLiveSearchStatus(groupName);
            const hasData = hasLocalData(groupInfo);

            return (
              <li key={groupName} className="mb-0.5">
                <button
                  className={`w-[calc(100%-16px)] flex items-center gap-2.5 px-3 py-2 mx-2 rounded-lg text-[13px] cursor-pointer transition-all duration-150 relative ${isEnabled ? 'bg-indigo-500/10' : ''}`}
                  style={{ color: isEnabled ? 'var(--text-primary)' : 'var(--text-muted)' }}
                  onClick={() => onSourceToggle(groupName)}
                >
                  {isEnabled && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 bg-indigo-500 rounded-r-full" />}
                  
                  {hasData ? (
                    <Database size={14} className={isEnabled ? "text-indigo-400" : ""} style={!isEnabled ? { color: 'var(--text-faint)' } : {}} />
                  ) : (
                    <div className="w-[14px] h-[14px]" />
                  )}
                  
                  <span className="flex-1 text-left truncate">{groupName}</span>
                  
                  {liveStatus === 'success' && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400/50" title="Live: Found" />}
                  {liveStatus === 'error' && <div className="w-1.5 h-1.5 rounded-full bg-rose-400/50" title="Live: Error" />}
                  {liveStatus === 'no_match' && <div className="w-1.5 h-1.5 rounded-full bg-amber-400/50" title="Live: No Matches" />}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default StatusPanel;